import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyLanguageCode, SurveyLanguageCodeDTO } from "../types/surveyLanguageCode";
/* eslint-disable import/export */
export abstract class RenameSurveyLanguageRPC implements
  LeoRPC<RenameSurveyLanguageRPC.Request, RenameSurveyLanguageRPC.Response, RenameSurveyLanguageRPC.Errors.Errors>
{
  abstract execute(
    request: RenameSurveyLanguageRPC.Request
  ): Promise<LeoRPCResult<RenameSurveyLanguageRPC.Response, RenameSurveyLanguageRPC.Errors.Errors>>;
}

export namespace RenameSurveyLanguageRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    languageId: string;
    languageCode: SurveyLanguageCodeDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isRenameSurveyLanguageRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly languageId: LeoUUID;
    readonly languageCode: SurveyLanguageCode;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    languageId: LeoUUID, 
    languageCode: SurveyLanguageCode
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.languageId = languageId;
      this.languageCode = languageCode;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const languageId = getLeoUuid(dto, "languageId", InvalidRequestLanguageIdError);
      const languageCode = SurveyLanguageCode.fromDTO(getObject(dto, "languageCode", InvalidRequestLanguageCodeError));
      return new Request(
        projectId,
        surveyId,
        languageId,
        languageCode
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          languageId: this.languageId.toDTO(),
          languageCode: this.languageCode.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `RenameSurveyLanguageRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',languageId='${this.languageId}',languageCode='${this.languageCode}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `RenameSurveyLanguageRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidLanguageId = "INVALID_LANGUAGE_ID",
    InvalidLanguageCode = "INVALID_LANGUAGE_CODE",
    BaseLanguageCannotBeModified = "BASE_LANGUAGE_CANNOT_BE_MODIFIED",
    DuplicateLanguageCode = "DUPLICATE_LANGUAGE_CODE",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    TranslationUpdateInProgress = "TRANSLATION_UPDATE_IN_PROGRESS"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `RenameSurveyLanguageRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `RenameSurveyLanguageRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidLanguageIdDTO {
      code: string;
    }
    
    export class InvalidLanguageId extends LeoRPCError<RPCError> {
      readonly isInvalidLanguageId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidLanguageId;
        super(code);
      }
    
    
      toDTO(): InvalidLanguageIdDTO {
        const dto: InvalidLanguageIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidLanguageId.prototype.toString = function toString(): string {
      return `RenameSurveyLanguageRPC.INVALID_LANGUAGE_ID()`;
    };
    
    
    export interface InvalidLanguageCodeDTO {
      code: string;
    }
    
    export class InvalidLanguageCode extends LeoRPCError<RPCError> {
      readonly isInvalidLanguageCode: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidLanguageCode;
        super(code);
      }
    
    
      toDTO(): InvalidLanguageCodeDTO {
        const dto: InvalidLanguageCodeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidLanguageCode.prototype.toString = function toString(): string {
      return `RenameSurveyLanguageRPC.INVALID_LANGUAGE_CODE()`;
    };
    
    
    export interface BaseLanguageCannotBeModifiedDTO {
      code: string;
    }
    
    export class BaseLanguageCannotBeModified extends LeoRPCError<RPCError> {
      readonly isBaseLanguageCannotBeModified: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.BaseLanguageCannotBeModified;
        super(code);
      }
    
    
      toDTO(): BaseLanguageCannotBeModifiedDTO {
        const dto: BaseLanguageCannotBeModifiedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    BaseLanguageCannotBeModified.prototype.toString = function toString(): string {
      return `RenameSurveyLanguageRPC.BASE_LANGUAGE_CANNOT_BE_MODIFIED()`;
    };
    
    
    export interface DuplicateLanguageCodeDTO {
      code: string;
    }
    
    export class DuplicateLanguageCode extends LeoRPCError<RPCError> {
      readonly isDuplicateLanguageCode: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DuplicateLanguageCode;
        super(code);
      }
    
    
      toDTO(): DuplicateLanguageCodeDTO {
        const dto: DuplicateLanguageCodeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DuplicateLanguageCode.prototype.toString = function toString(): string {
      return `RenameSurveyLanguageRPC.DUPLICATE_LANGUAGE_CODE()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `RenameSurveyLanguageRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface TranslationUpdateInProgressDTO {
      code: string;
    }
    
    export class TranslationUpdateInProgress extends LeoRPCError<RPCError> {
      readonly isTranslationUpdateInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.TranslationUpdateInProgress;
        super(code);
      }
    
    
      toDTO(): TranslationUpdateInProgressDTO {
        const dto: TranslationUpdateInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    TranslationUpdateInProgress.prototype.toString = function toString(): string {
      return `RenameSurveyLanguageRPC.TRANSLATION_UPDATE_IN_PROGRESS()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | InvalidLanguageId | InvalidLanguageCode | BaseLanguageCannotBeModified | DuplicateLanguageCode | ProjectAlreadyArchived | TranslationUpdateInProgress;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | InvalidLanguageIdDTO | InvalidLanguageCodeDTO | BaseLanguageCannotBeModifiedDTO | DuplicateLanguageCodeDTO | ProjectAlreadyArchivedDTO | TranslationUpdateInProgressDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidLanguageId:
          return new InvalidLanguageId();
        case RPCError.InvalidLanguageCode:
          return new InvalidLanguageCode();
        case RPCError.BaseLanguageCannotBeModified:
          return new BaseLanguageCannotBeModified();
        case RPCError.DuplicateLanguageCode:
          return new DuplicateLanguageCode();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.TranslationUpdateInProgress:
          return new TranslationUpdateInProgress();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends RenameSurveyLanguageRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends RenameSurveyLanguageRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestLanguageIdError extends RenameSurveyLanguageRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLanguageIdError";
  }
}

export class InvalidRequestLanguageCodeError extends RenameSurveyLanguageRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLanguageCodeError";
  }
}


export class InvalidResponseInvalidProjectIdError extends RenameSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends RenameSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidLanguageIdError extends RenameSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidLanguageIdError";
  }
}

export class InvalidResponseInvalidLanguageCodeError extends RenameSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidLanguageCodeError";
  }
}

export class InvalidResponseBaseLanguageCannotBeModifiedError extends RenameSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseBaseLanguageCannotBeModifiedError";
  }
}

export class InvalidResponseDuplicateLanguageCodeError extends RenameSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDuplicateLanguageCodeError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends RenameSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseTranslationUpdateInProgressError extends RenameSurveyLanguageRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTranslationUpdateInProgressError";
  }
}

